import { PositionType } from 'data/types';
import {
  AutoExtendedEdge,
  isPolygonInsideFootprint,
  isPolygonSimple,
  polygonArea,
  polygonOverlapsAny,
  SpaceShapeId
} from './space-polygon';

const TRIANGLE_SHAPE_IDS: ReadonlySet<SpaceShapeId> = new Set<SpaceShapeId>([
  'triangle',
  'triangle-up',
  'triangle-right',
  'triangle-down',
  'triangle-left'
]);

export function isTriangleShape(shape: SpaceShapeId): boolean {
  return TRIANGLE_SHAPE_IDS.has(shape);
}

const MIN_TRIANGLE_AREA = 0.5;
const COINCIDENT_VERTEX_EPS = 1e-9;

function verticesAreCoincident(a: PositionType, b: PositionType): boolean {
  return (
    Math.abs(a.x - b.x) < COINCIDENT_VERTEX_EPS &&
    Math.abs(a.y - b.y) < COINCIDENT_VERTEX_EPS
  );
}

function hasCoincidentVertices(verts: PositionType[]): boolean {
  for (let i = 0; i < verts.length; i += 1) {
    for (let j = i + 1; j < verts.length; j += 1) {
      if (verticesAreCoincident(verts[i], verts[j])) return true;
    }
  }
  return false;
}

function isValidTriangle(
  candidate: PositionType[],
  footprint: PositionType[] | null,
  otherSpacePolygons: PositionType[][]
): boolean {
  if (candidate.length !== 3) return false;
  if (hasCoincidentVertices(candidate)) return false;
  if (polygonArea(candidate) < MIN_TRIANGLE_AREA) return false;
  if (!isPolygonSimple(candidate)) return false;
  if (
    footprint &&
    footprint.length >= 3 &&
    !isPolygonInsideFootprint(candidate, footprint)
  ) {
    return false;
  }
  return !polygonOverlapsAny(candidate, otherSpacePolygons);
}

function totalManhattanDisplacement(
  original: PositionType[],
  candidate: PositionType[]
): number {
  let total = 0;
  for (let i = 0; i < original.length; i += 1) {
    total +=
      Math.abs(original[i].x - candidate[i].x) +
      Math.abs(original[i].y - candidate[i].y);
  }
  return total;
}

function buildCandidateFromIndex(
  absVerts: PositionType[],
  index: number
): PositionType[] {
  let remaining = index;
  return absVerts.map((v) => {
    const useCeilX = remaining % 2 === 1;
    remaining = Math.floor(remaining / 2);
    const useCeilY = remaining % 2 === 1;
    remaining = Math.floor(remaining / 2);
    return {
      x: useCeilX ? Math.ceil(v.x) : Math.floor(v.x),
      y: useCeilY ? Math.ceil(v.y) : Math.floor(v.y)
    };
  });
}

export function snapTriangleVerticesToGrid(
  absVerts: PositionType[],
  footprint: PositionType[] | null,
  otherSpacePolygons: PositionType[][]
): PositionType[] | null {
  if (absVerts.length !== 3) return null;

  const rounded: PositionType[] = absVerts.map((v) => ({
    x: Math.round(v.x),
    y: Math.round(v.y)
  }));
  if (isValidTriangle(rounded, footprint, otherSpacePolygons)) {
    return rounded;
  }

  const totalCombinations = 2 ** (absVerts.length * 2);
  let best: PositionType[] | null = null;
  let bestDisplacement = Infinity;
  for (let index = 0; index < totalCombinations; index += 1) {
    const candidate = buildCandidateFromIndex(absVerts, index);
    if (isValidTriangle(candidate, footprint, otherSpacePolygons)) {
      const displacement = totalManhattanDisplacement(absVerts, candidate);
      if (displacement < bestDisplacement) {
        bestDisplacement = displacement;
        best = candidate;
      }
    }
  }
  return best;
}

function vertexKey(point: PositionType): string {
  return `${point.x}_${point.y}`;
}

function buildVertexSnapMap(
  preSnap: PositionType[],
  postSnap: PositionType[]
): Map<string, PositionType> {
  const map = new Map<string, PositionType>();
  for (let i = 0; i < preSnap.length; i += 1) {
    map.set(vertexKey(preSnap[i]), postSnap[i]);
  }
  return map;
}

function lookupSnappedVertex(
  map: Map<string, PositionType>,
  point: PositionType
): PositionType {
  return map.get(vertexKey(point)) ?? point;
}

export function remapExtensionRecordsAfterSnap(
  preSnap: PositionType[],
  postSnap: PositionType[],
  records: AutoExtendedEdge[]
): AutoExtendedEdge[] {
  if (records.length === 0) return records;
  const snapMap = buildVertexSnapMap(preSnap, postSnap);
  return records.map((record) => ({
    prevA: record.prevA,
    prevB: record.prevB,
    currA: lookupSnappedVertex(snapMap, record.currA),
    currB: lookupSnappedVertex(snapMap, record.currB)
  }));
}
